/**
 * Resume dropzone — drag/drop or browse. Validates type (.pdf/.docx/.doc/.txt)
 * and size (≤10 MB) before handing the File to the parent. Controlled component.
 */

import React, { useRef, useState } from 'react';
import { UploadCloud, FileText, X } from 'lucide-react';
import { cn } from '../../../utils/cn';

const ACCEPT = ['.pdf', '.docx', '.doc', '.txt'];
const MAX_BYTES = 10 * 1024 * 1024;

const isAllowed = (name: string) => ACCEPT.some(ext => name.toLowerCase().endsWith(ext));
const prettySize = (bytes: number) =>
	bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

interface ResumeDropzoneProps {
	file: File | null;
	onFile: (file: File | null) => void;
	disabled?: boolean;
	hint?: string;
	className?: string;
}

export const ResumeDropzone: React.FC<ResumeDropzoneProps> = ({ file, onFile, disabled, hint, className }) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [dragOver, setDragOver] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const accept = (f: File | undefined | null) => {
		if (!f) return;
		if (!isAllowed(f.name)) {
			setError('Unsupported file — use PDF, DOCX, DOC or TXT.');
			return;
		}
		if (f.size > MAX_BYTES) {
			setError('That file is over 10 MB. Please upload a smaller file.');
			return;
		}
		setError(null);
		onFile(f);
	};

	if (file) {
		return (
			<div className={cn('rounded-xl border border-gray-200 bg-white p-3', className)}>
				<div className='flex items-center gap-3'>
					<div className='flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-700'>
						<FileText className='h-5 w-5' />
					</div>
					<div className='min-w-0 flex-1'>
						<p className='truncate text-sm font-medium text-gray-900'>{file.name}</p>
						<p className='text-xs text-gray-500'>{prettySize(file.size)}</p>
					</div>
					{!disabled && (
						<button
							type='button'
							onClick={() => {
								onFile(null);
								setError(null);
								if (inputRef.current) inputRef.current.value = '';
							}}
							className='rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
							aria-label='Remove file'
						>
							<X className='h-4 w-4' />
						</button>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className={className}>
			<button
				type='button'
				disabled={disabled}
				onClick={() => inputRef.current?.click()}
				onDragOver={e => {
					e.preventDefault();
					if (!disabled) setDragOver(true);
				}}
				onDragLeave={() => setDragOver(false)}
				onDrop={e => {
					e.preventDefault();
					setDragOver(false);
					if (disabled) return;
					accept(e.dataTransfer.files?.[0]);
				}}
				className={cn(
					'flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors',
					dragOver ? 'border-primary bg-primary-50/50' : 'border-gray-300 bg-gray-50 hover:border-primary/50',
					disabled && 'cursor-not-allowed opacity-60'
				)}
			>
				<UploadCloud className='h-7 w-7 text-gray-400' />
				<span className='text-sm font-medium text-gray-700'>Drop the resume here, or browse</span>
				<span className='text-xs text-gray-400'>{hint ?? 'PDF, DOCX, DOC or TXT — up to 10 MB'}</span>
			</button>
			<input
				ref={inputRef}
				type='file'
				accept={ACCEPT.join(',')}
				className='hidden'
				onChange={e => accept(e.target.files?.[0])}
			/>
			{error && <p className='mt-2 text-sm text-red-600'>{error}</p>}
		</div>
	);
};
